import React, { useMemo } from 'react';
import { View, Pressable } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react-native';
import { useQuery } from '@apollo/client/react';
import { Text } from '@/components/ui/text';
import { useLocalizedValue } from '@/hooks/useLocalizedValue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { useModal } from '@/contexts/ModalContext';
import { CurrentUserDocument } from '~/graphql/generated/graphql';
import { SportActivityFormDialog } from '~/sport-activity/components/sport-activity-form-dialog/SportActivityFormDialog';
import {
  Card,
  CardAction,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import type {
  MuscleTargetRecord,
  SportActivityCategoryRecord,
  SportActivityRecord,
} from '~/powersync/generated/schema';
import type { LocalizedString } from '@fytness/shared';

type SportActivityWithCategory = Pick<
  SportActivityRecord,
  | 'id'
  | 'name'
  | 'categoryId'
  | 'userId'
  | 'createdAt'
  | 'updatedAt'
  | 'deletedAt'
> & {
  category?: {
    id: string;
    title?: LocalizedString;
    shortDescription?: LocalizedString;
  };
  muscleTargets: Array<
    Pick<MuscleTargetRecord, 'id' | 'muscle' | 'muscleGroup' | 'intensity'>
  >;
};

interface SportActivityCardProps {
  activity: SportActivityWithCategory;
  className?: string;
  onPress?: () => void;
}

export const SportActivityCard: React.FC<SportActivityCardProps> & {
  Synced: typeof SportActivityCardSynced;
} = ({ activity, className, onPress }) => {
  const { t } = useTranslation();
  const { showModal } = useModal();
  const { data: userData } = useQuery(CurrentUserDocument);
  const currentUserId = userData?.currentUser?.id;

  const localizedName = useLocalizedValue(activity.name);
  const localizedCategoryTitle = useLocalizedValue(activity.category?.title);
  const localizedCategoryShortDescription = useLocalizedValue(
    activity.category?.shortDescription,
  );
  const intensitySortedMuscleGroups = useMemo(
    () => activity.muscleTargets.sort((a, b) => b.intensity - a.intensity),
    [activity.muscleTargets],
  );

  // Show edit button for custom activities (userId exists) owned by current user
  const isOwner = !!activity.userId && activity.userId === currentUserId;

  const handleEdit = () => {
    showModal(SportActivityFormDialog, {
      sportActivityId: activity.id,
    });
  };

  return (
    <Card className={className}>
      <CardHeader className="flex-row w-full justify-between items-start">
        {activity.name ? (
          <>
            <Pressable
              onPress={onPress}
              className="flex flex-col flex-1"
              disabled={!onPress}
            >
              {activity.category && (
                <CardDescription>
                  {localizedCategoryTitle || localizedCategoryShortDescription}
                </CardDescription>
              )}
              <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
                {localizedName}
              </CardTitle>
            </Pressable>
            <View className="flex-row items-center gap-2">
              {activity.muscleTargets.length > 0 && (
                <CardAction>
                  <Badge variant="secondary">
                    <Text>
                      {t(
                        `chat:response.muscleGroups.${intensitySortedMuscleGroups[0].muscleGroup}`,
                      )}
                    </Text>
                  </Badge>
                </CardAction>
              )}
              {isOwner && (
                <Button
                  variant="ghost"
                  size="sm"
                  onPress={handleEdit}
                  className="h-8 w-8 p-0"
                >
                  <Icon
                    as={Pencil}
                    size={16}
                    className="text-muted-foreground"
                  />
                </Button>
              )}
            </View>
          </>
        ) : (
          <>
            <View className="flex flex-col flex-1">
              {/* Skeleton for category description */}
              <View className="h-4 w-24 bg-muted rounded mb-2" />
              {/* Skeleton for activity name */}
              <View className="h-8 w-48 bg-muted rounded" />
            </View>
            <View className="flex-row items-center gap-2">
              {/* Skeleton for badge */}
              <View className="h-6 w-20 bg-muted rounded" />
            </View>
          </>
        )}
      </CardHeader>
    </Card>
  );
};

interface SportActivityCardSyncedProps {
  id: string;
  className?: string;
  onPress?: () => void;
}

const SportActivityCardSynced: React.FC<SportActivityCardSyncedProps> = ({
  id,
  className,
  onPress,
}) => {
  // Fetch sport activity with live query
  const { data: sportActivities } = useLivePowerSyncQuery(
    db.SportActivity.queries.findById(id),
    [id],
  );

  const sportActivity = sportActivities?.[0];

  // Fetch muscle targets with live query
  const { data: muscleTargets } = useLivePowerSyncQuery<MuscleTargetRecord>(
    db.SportActivity.queries.findMuscleTargets(id),
    [id],
  );

  // Fetch category with live query
  const { data: categories } =
    useLivePowerSyncQuery<SportActivityCategoryRecord>(
      sportActivity?.categoryId
        ? db.SportActivity.queries.findCategoryById(sportActivity.categoryId)
        : null,
      [sportActivity?.categoryId],
    );

  const category = categories?.[0];

  const activityWithTargets: SportActivityWithCategory = {
    ...sportActivity,
    muscleTargets: muscleTargets || [],
    category,
  };

  return (
    <SportActivityCard
      activity={activityWithTargets}
      className={className}
      onPress={onPress}
    />
  );
};

// Attach Synced as a static property
SportActivityCard.Synced = SportActivityCardSynced;
